import request, { merchantApi } from '@/config/axios'
import { getEducationMembershipId } from '@/views/aiEducation/composables/educationMembershipContext'

const withEducationMembership = (option: Record<string, any>) => {
  const membershipId = getEducationMembershipId()
  return membershipId
    ? {
        ...option,
        headers: {
          ...(option.headers || {}),
          'ai-education-membership-id': String(membershipId)
        }
      }
    : option
}

export const educationRequest = {
  get: <T = any>(option: Record<string, any>) => request.get<T>(withEducationMembership(option)),
  post: <T = any>(option: Record<string, any>) => request.post<T>(withEducationMembership(option)),
  put: <T = any>(option: Record<string, any>) => request.put<T>(withEducationMembership(option)),
  delete: <T = any>(option: Record<string, any>) =>
    request.delete<T>(withEducationMembership(option)),
  download: <T = any>(option: Record<string, any>) =>
    request.download<T>(withEducationMembership(option)),
  upload: <T = any>(option: Record<string, any>) =>
    request.upload<T>(withEducationMembership(option))
}

// 学生工作台使用 app-api 控制器；通过根地址实例保留与管理端相同的认证拦截器。
const studentRequest = {
  get: <T = any>(option: Record<string, any>) =>
    merchantApi.get<T>(withEducationMembership({ ...option, url: `/app-api${option.url}` })),
  put: <T = any>(option: Record<string, any>) =>
    merchantApi.put<T>(withEducationMembership({ ...option, url: `/app-api${option.url}` }))
}

export interface EducationDataScopeVO {
  schoolIds: number[]
  classIds: number[]
  teachingClassIds: number[]
  studentIds: number[]
  childStudentIds: number[]
}

export interface EducationOrganizationOptionVO {
  membershipId?: number
  organizationType: string
  organizationId: number
  organizationName: string
  workspace: string
  defaultFlag: boolean
  /** 学段字典 ai_edu_stage */
  eduStage?: string
  parentEnabled?: boolean
  deviceEnabled?: boolean
}

export interface CurrentEducationWorkspaceVO {
  userId: number
  tenantId: number
  membershipId?: number
  organizationType: string
  organizationId: number
  organizationName: string
  workspace: string
  roles: string[]
  permissions: string[]
  dataScope: EducationDataScopeVO
  organizations: EducationOrganizationOptionVO[]
}

export const getCurrentEducationWorkspace = (membershipId?: number) =>
  educationRequest.get<CurrentEducationWorkspaceVO>({
    url: '/ai-education/workspace/current',
    params: { membershipId }
  })

export interface StudentAssignmentVO {
  id: number
  trainCourseId: number
  title: string
  description?: string
  dueTime?: string
  maxScore?: number
  expired?: boolean
  submissionId?: number
  submissionStatus?: number
  submissionContent?: string
  score?: number
  teacherFeedback?: string
  submitTime?: string
}

export const getMyStudentAssignments = () =>
  studentRequest.get<StudentAssignmentVO[]>({ url: '/ai-education/student/assignment/my-list' })

export interface StudentLearningPlanVO {
  id: number
  title?: string
  description?: string
  status?: number
  startDate?: string
  endDate?: string
}

export interface StudentLearningPlanItemVO {
  id: number
  planId: number
  title?: string
  description?: string
  status?: number
  dueDate?: string
}

export const getMyStudentLearningPlans = () =>
  studentRequest.get<StudentLearningPlanVO[]>({
    url: '/ai-education/student/learning-plan/my-list'
  })
export const getStudentLearningPlanItems = (planId: number) =>
  studentRequest.get<StudentLearningPlanItemVO[]>({
    url: '/ai-education/student/learning-plan/item-list',
    params: { planId }
  })
export const completeStudentLearningPlanItem = (itemId: number) =>
  studentRequest.put<boolean>({
    url: '/ai-education/student/learning-plan/complete-item',
    params: { itemId }
  })

export interface StudentSummaryVO {
  studentId?: number
  trainCourseId?: number
  studySeconds: number
  completedChapters: number
  quizAttempts: number
  correctAnswers: number
  wrongAnswers: number
  assignmentsSubmitted: number
  aiRequests: number
  aiRefusals: number
  correctRate: number
  aiRefusalRate: number
}

export const getMyStudentSummary = (trainCourseId?: number) =>
  studentRequest.get<StudentSummaryVO>({
    url: '/ai-education/student/analytics/my-summary',
    params: { trainCourseId }
  })

export const getParentChildSummary = (studentId: number, trainCourseId?: number) =>
  studentRequest.get<StudentSummaryVO>({
    url: '/ai-education/parent/analytics/child-summary',
    params: { studentId, trainCourseId }
  })

export interface ParentChildVO {
  studentId: number
  nickname?: string
  avatar?: string
}

export const getParentChildren = () =>
  studentRequest.get<ParentChildVO[]>({ url: '/ai-education/parent/child/list' })

export interface ParentChildAssignmentVO {
  id: number
  trainCourseId: number
  title: string
  description?: string
  dueTime?: string
  maxScore?: number
  expired?: boolean
  submissionStatus?: number
  submitTime?: string
  score?: number
  teacherFeedback?: string
  reviewTime?: string
}

export const getParentChildAssignments = (studentId: number) =>
  studentRequest.get<ParentChildAssignmentVO[]>({
    url: '/ai-education/parent/assignment/child-list',
    params: { studentId }
  })

export interface StudentCourseVO {
  id: number
  name?: string
  subtitle?: string
  cover?: string
  description?: string
  teacherName?: string
  progress?: number
  chapters?: StudentChapterVO[]
}

export interface StudentChapterVO {
  id: number
  name?: string
  title?: string
  sort?: number
  progress?: number
  accessible?: boolean
  learningStatus?: string
  lockReason?: string
  isFinished?: boolean
}

export interface StudentCoursePageVO {
  list: StudentCourseVO[]
  total: number
}

export const getMyStudentCoursePage = (pageNo = 1, pageSize = 12) =>
  studentRequest.get<StudentCoursePageVO>({
    url: '/train/my-course/page',
    params: { pageNo, pageSize }
  })

export interface StudentCourseContextVO {
  schoolId: number
  teachingClassId: number
  trainCourseId: number
}

export interface StudentAiCitationVO {
  segmentId: number
  documentId: number
  content: string
  similarity?: number
}

export const getStudentCourseContext = (trainCourseId: number) =>
  studentRequest.get<StudentCourseContextVO>({
    url: '/ai-education/student/course/context',
    params: { trainCourseId }
  })

export interface SchoolVO {
  id?: number
  name: string
  code: string
  deptId?: number
  eduStage?: string
  parentEnabled?: boolean
  deviceEnabled?: boolean
  defaultLanguage?: string
  timezone?: string
  status?: number
  contactName?: string
  contactMobile?: string
}

export interface CoursePackageVO {
  id?: number
  name: string
  code: string
  description?: string
  targetGrades?: string
  languages?: string
  status?: number
  currentVersion?: number
}

export interface CoursePackageVersionVO {
  id: number
  packageId: number
  versionNo: number
  status: number
  knowledgeSnapshotJson?: string
  publishedTime?: string
  courses?: PackageCourseVO[]
}

export interface PackageCourseVO {
  id: number
  packageVersionId: number
  trainCourseId: number
  sort: number
  requiredFlag: boolean
}

export interface PackageLicenseVO {
  id: number
  schoolId: number
  packageVersionId: number
  startTime: string
  endTime: string
  languages?: string
  aiTokenQuota?: number
  status?: number
}

export interface AiPolicyVO {
  id?: number
  schoolId: number
  name: string
  aiModelId: number
  minAge?: number
  maxAge?: number
  dailyTokenLimit?: number
  retentionDays?: number
  sensitiveWords?: string
  citationRequired?: boolean
  enabled?: boolean
}

export interface KnowledgeBindingVO {
  id?: number
  schoolId: number
  trainCourseId: number
  knowledgeId: number
  similarityThreshold?: number
  topK?: number
  enabled?: boolean
}

export interface AssignmentSubmissionVO {
  id: number
  assignmentId: number
  studentId: number
  attemptNo: number
  status: number
  content?: string
  submitTime?: string
  score?: number
  teacherFeedback?: string
}

export interface AiFeedbackVO {
  id: number
  interactionId: number
  userId: number
  helpful: boolean
  comment?: string
  escalateToTeacher: boolean
  status: number
}

export interface AssignmentVO {
  id?: number
  teachingClassId: number
  trainCourseId: number
  chapterId?: number
  title: string
  description?: string
  status?: number
  dueTime: string
  maxScore?: number
  allowResubmit?: boolean
  maxAttempts?: number
  targets?: Array<{ targetType: number; targetId: number }>
}

export interface AnalyticsSummaryVO {
  studentId?: number
  trainCourseId?: number
  studySeconds: number
  completedChapters: number
  quizAttempts: number
  correctAnswers: number
  wrongAnswers: number
  assignmentsSubmitted: number
  aiRequests: number
  aiRefusals: number
  correctRate: number
  aiRefusalRate: number
}

export interface ContentDraftVO {
  id?: number
  trainCourseId: number
  chapterId?: number
  contentType: number
  title: string
  content: string
  sourceType?: string
  sourceInteractionId?: number
  knowledgeId?: number
  aiModelId?: number
  promptVersion?: string
  evidenceJson?: string
  inputTokens?: number
  outputTokens?: number
  generatedTime?: string
  status?: number
  version?: number
}

export interface AiEvaluationCaseVO {
  id?: number
  name: string
  schoolId: number
  trainCourseId: number
  question: string
  expectedAnswerKeywords?: string
  expectedCitationKeywords?: string
  language?: string
  category?: string
  enabled?: boolean
}

export interface AiEvaluationResultVO {
  id: number
  caseId: number
  runBatchId: string
  actualAnswer?: string
  citationCount: number
  answerMatched: boolean
  citationMatched: boolean
  passed: boolean
  score: number
  latencyMs?: number
  errorMessage?: string
  evaluatedTime: string
}

export interface ContentLicenseVO {
  id?: number
  trainCourseId: number
  contentType: string
  copyrightOwner: string
  licenseType: string
  licenseNo?: string
  allowedRegions?: string
  allowedLanguages?: string
  startTime: string
  endTime: string
  proofFileUrl?: string
  remark?: string
  status?: number
}

export interface ClassVO {
  id: number
  schoolId: number
  name: string
  code?: string
  campusId?: number
  gradeId?: number
  headTeacherId?: number
  schoolYear?: number
  status?: number
}

export interface CampusVO {
  id?: number
  schoolId: number
  name: string
  code: string
  address?: string
  status?: number
}

export interface GradeVO {
  id?: number
  schoolId: number
  name: string
  stage?: string
  sort?: number
}

export interface TeachingClassVO {
  id?: number
  schoolId: number
  schoolName?: string
  termId?: number
  termName?: string
  classId?: number
  offerDeptId?: number
  trainCourseId?: number
  /** 后端填充的培训课名称 */
  trainCourseName?: string
  name: string
  teacherId?: number
  capacity?: number
  credit?: number
  waitlistEnabled?: boolean
  startTime?: string
  endTime?: string
  status?: number
  createTime?: string
}

export interface TeachingClassStudentVO {
  id?: number
  teachingClassId?: number
  studentId: number
  studentName?: string
  nickname?: string
  mobile?: string
  avatar?: string
  status?: number
}

export interface MemberImportRowVO {
  classId: number
  teachingClassId?: number
  mobile: string
  password?: string
  userType: number
  existingUserId?: number
}

export interface ClassInviteVO {
  id: number
  schoolId: number
  classId: number
  teachingClassId?: number
  inviteCode: string
  expireTime: string
  maxUses: number
  usedCount: number
  status: number
}
export interface ResourceTaskVO {
  id: number
  schoolId: number
  trainCourseId: number
  chapterId?: number
  knowledgeId: number
  fileName: string
  fileUrl: string
  fileType: string
  fileSize?: number
  segmentMaxTokens: number
  status: number
  aiDocumentId?: number
  retryCount: number
  errorMessage?: string
  createTime?: string
}

export const getSchoolList = () => educationRequest.get({ url: '/ai-education/school/list' })
export const getTrainCourseOptions = (limit = 200) =>
  educationRequest.get<{ id: number; name?: string; title?: string; status?: number }[]>({
    url: '/ai-education/option/train-course/list',
    params: { limit },
    headers: { ignoreError: true }
  })
export const getSchoolOptions = () =>
  educationRequest.get({
    url: '/ai-education/option/school/list',
    headers: { ignoreError: true }
  })
export const getClassList = (schoolId?: number) =>
  educationRequest.get({ url: '/ai-education/class/list', params: { schoolId } })
export const saveClass = (data: Partial<ClassVO>) =>
  educationRequest.post({ url: '/ai-education/class/save', data })
export const deleteClass = (id: number) =>
  educationRequest.delete({ url: '/ai-education/class/delete', params: { id } })
export const getCampusList = (schoolId?: number) =>
  educationRequest.get({ url: '/ai-education/campus/list', params: { schoolId } })
export const saveCampus = (data: CampusVO) =>
  educationRequest.post({ url: '/ai-education/campus/save', data })
export const deleteCampus = (id: number) =>
  educationRequest.delete({ url: '/ai-education/campus/delete', params: { id } })
export const getGradeList = (schoolId?: number) =>
  educationRequest.get({ url: '/ai-education/grade/list', params: { schoolId } })
export const saveGrade = (data: GradeVO) =>
  educationRequest.post({ url: '/ai-education/grade/save', data })
export const deleteGrade = (id: number) =>
  educationRequest.delete({ url: '/ai-education/grade/delete', params: { id } })
export const saveSchool = (data: SchoolVO) =>
  educationRequest.post({ url: '/ai-education/school/save', data })
export const deleteSchool = (id: number) =>
  educationRequest.delete({ url: `/ai-education/school/delete?id=${id}` })

/** ---------- K12-P0 家长关系 ---------- */
export interface ParentRelationVO {
  id?: number
  schoolId?: number
  schoolName?: string
  parentId: number
  parentName?: string
  parentMobile?: string
  studentId: number
  studentName?: string
  studentMobile?: string
  relationship: string
  status?: number
  verifiedTime?: string
  startTime?: string
  endTime?: string
  createTime?: string
}

export interface ParentMemberOptionVO {
  id: number
  nickname?: string
  mobile?: string
  workspace?: string
}

export const getParentRelationList = (params?: {
  schoolId?: number
  parentId?: number
  studentId?: number
}) =>
  educationRequest.get<ParentRelationVO[]>({
    url: '/ai-education/parent-relation/list',
    params
  })

export const saveParentRelation = (data: Partial<ParentRelationVO> & {
  schoolId: number
  parentId: number
  studentId: number
  relationship: string
}) => educationRequest.post<number>({ url: '/ai-education/parent-relation/save', data })

export const disableParentRelation = (id: number) =>
  educationRequest.put({ url: '/ai-education/parent-relation/disable', params: { id } })

export const deleteParentRelation = (id: number) =>
  educationRequest.delete({ url: '/ai-education/parent-relation/delete', params: { id } })

export const getParentMemberOptions = (params: {
  schoolId: number
  workspace: 'parent' | 'student'
  keyword?: string
}) =>
  educationRequest.get<ParentMemberOptionVO[]>({
    url: '/ai-education/parent-relation/member-options',
    params
  })

export const getCoursePackageList = () =>
  educationRequest.get({
    url: '/ai-education/course-package/list',
    headers: { ignoreError: true }
  })
export const saveCoursePackage = (data: CoursePackageVO) =>
  educationRequest.post({ url: '/ai-education/course-package/save', data })
export const publishCoursePackage = (id: number) =>
  educationRequest.put({ url: `/ai-education/course-package/publish?id=${id}` })
export const getPackageVersionList = (packageId: number) =>
  educationRequest.get({ url: '/ai-education/course-package/version/list', params: { packageId } })
export const getPackageVersion = (id: number) =>
  educationRequest.get({ url: '/ai-education/course-package/version/get', params: { id } })
export const createPackageVersion = (data: {
  packageId: number
  knowledgeSnapshotJson?: string
  courses: Array<{ trainCourseId: number; sort: number; requiredFlag: boolean }>
}) => educationRequest.post({ url: '/ai-education/course-package/version', data })
export const publishPackageVersion = (id: number) =>
  educationRequest.put({ url: '/ai-education/course-package/version/publish', params: { id } })
export const getPackageLicenseList = (schoolId?: number) =>
  educationRequest.get({ url: '/ai-education/course-package/license/list', params: { schoolId } })
export const grantPackageLicense = (data: {
  schoolId: number
  packageVersionId: number
  startTime: string
  endTime: string
  languages?: string
  aiTokenQuota?: number
  status?: number
}) => educationRequest.post({ url: '/ai-education/course-package/license', data })
export const revokePackageLicense = (id: number) =>
  educationRequest.put({ url: '/ai-education/course-package/license/revoke', params: { id } })

export const getAiPolicyList = (schoolId?: number) =>
  educationRequest.get({
    url: '/ai-education/ai-policy/list',
    params: { schoolId },
    headers: { ignoreError: true }
  })
export const saveAiPolicy = (data: AiPolicyVO) =>
  educationRequest.post({ url: '/ai-education/ai-policy/save', data })

export const getKnowledgeBindingList = (params?: { schoolId?: number; trainCourseId?: number }) =>
  educationRequest.get({ url: '/ai-education/learning/knowledge-binding/list', params })
export const saveKnowledgeBinding = (data: KnowledgeBindingVO) =>
  educationRequest.post({ url: '/ai-education/learning/knowledge-binding/save', data })

export const getAssignmentSubmissionList = (assignmentId: number) =>
  educationRequest.get({ url: '/ai-education/learning/submission/list', params: { assignmentId } })
export const reviewAssignmentSubmission = (data: {
  submissionId: number
  score: number
  feedback?: string
  returnForResubmit: boolean
}) => educationRequest.put({ url: '/ai-education/learning/submission/review', data })

export const getEscalatedAiFeedbackList = (status?: number) =>
  educationRequest.get({
    url: '/ai-education/learning/ai-feedback/list',
    params: { status },
    headers: { ignoreError: true }
  })
export const resolveAiFeedback = (id: number) =>
  educationRequest.put({ url: '/ai-education/learning/ai-feedback/resolve', params: { id } })

export const getAssignmentList = (teachingClassId?: number) =>
  educationRequest.get({
    url: '/ai-education/assignment/list',
    params: { teachingClassId },
    headers: { ignoreError: true }
  })
export const saveAssignment = (data: AssignmentVO) =>
  educationRequest.post({ url: '/ai-education/assignment/save', data })
export const publishAssignment = (id: number) =>
  educationRequest.put({ url: '/ai-education/assignment/publish', params: { id } })
export const getAssignmentStats = (assignmentId: number) =>
  educationRequest.get({ url: '/ai-education/learning/submission/stats', params: { assignmentId } })

export const getAnalyticsSummary = (params?: {
  studentId?: number
  trainCourseId?: number
  startDate?: string
  endDate?: string
}) =>
  educationRequest.get({
    url: '/ai-education/analytics/summary',
    params,
    headers: { ignoreError: true }
  })

export const getPrincipalAnalyticsSummary = () =>
  educationRequest.get<StudentSummaryVO>({
    url: '/ai-education/analytics/principal-summary',
    headers: { ignoreError: true }
  })
export type AnalyticsQuery = {
  studentId?: number
  trainCourseId?: number
  startDate?: string
  endDate?: string
}
export const exportAnalyticsExcel = (params?: AnalyticsQuery) =>
  educationRequest.download({ url: '/ai-education/analytics/export-excel', params })
export const exportAnalyticsPdf = (params?: AnalyticsQuery) =>
  educationRequest.download({ url: '/ai-education/analytics/export-pdf', params })

export const getContentDraftList = (params?: { trainCourseId?: number; status?: number }) =>
  educationRequest.get({
    url: '/ai-education/content-draft/list',
    params,
    headers: { ignoreError: true }
  })
export const saveContentDraft = (data: ContentDraftVO) =>
  educationRequest.post({ url: '/ai-education/content-draft/save', data })
export const submitContentDraft = (id: number) =>
  educationRequest.put({ url: '/ai-education/content-draft/submit', params: { id } })
export const reviewContentDraft = (data: {
  draftId: number
  approved: boolean
  comment?: string
}) => educationRequest.put({ url: '/ai-education/content-draft/review', data })
export const publishContentDraft = (id: number) =>
  educationRequest.put({ url: '/ai-education/content-draft/publish', params: { id } })
export const getContentReviewList = (draftId: number) =>
  educationRequest.get({ url: '/ai-education/content-draft/reviews', params: { draftId } })

export const getEvaluationCaseList = (params?: { schoolId?: number; trainCourseId?: number }) =>
  educationRequest.get({ url: '/ai-education/governance/evaluation-case/list', params })
export const saveEvaluationCase = (data: AiEvaluationCaseVO) =>
  educationRequest.post({ url: '/ai-education/governance/evaluation-case/save', data })
export const runEvaluationCase = (id: number) =>
  educationRequest.post({ url: '/ai-education/governance/evaluation-case/run', params: { id } })
export const getEvaluationResultList = (caseId?: number) =>
  educationRequest.get({
    url: '/ai-education/governance/evaluation-result/list',
    params: { caseId }
  })
export const getContentLicenseList = (params?: { trainCourseId?: number; status?: number }) =>
  educationRequest.get({ url: '/ai-education/governance/content-license/list', params })
export const saveContentLicense = (data: ContentLicenseVO) =>
  educationRequest.post({ url: '/ai-education/governance/content-license/save', data })
export const disableContentLicense = (id: number) =>
  educationRequest.put({ url: '/ai-education/governance/content-license/disable', params: { id } })

export const getTeachingClassList = (params?: { schoolId?: number; teacherId?: number }) =>
  educationRequest.get<TeachingClassVO[]>({
    url: '/ai-education/teaching-class/list',
    params,
    headers: { ignoreError: true }
  })
export const getTeachingClass = (id: number) =>
  educationRequest.get<TeachingClassVO>({ url: '/ai-education/teaching-class/get', params: { id } })
export const saveTeachingClass = (data: any) =>
  educationRequest.post({ url: '/ai-education/teaching-class/save', data })
export const replaceTeachingClassStudents = (data: {
  teachingClassId: number
  studentIds: number[]
}) => educationRequest.put({ url: '/ai-education/teaching-class/students', data })
export const getTeachingClassStudents = (teachingClassId: number) =>
  educationRequest.get<TeachingClassStudentVO[]>({
    url: '/ai-education/teaching-class/students',
    params: { teachingClassId }
  })
export const deleteTeachingClass = (id: number) =>
  educationRequest.delete({ url: '/ai-education/teaching-class/delete', params: { id } })

export const importMembers = (rows: MemberImportRowVO[]) =>
  educationRequest.post({ url: '/ai-education/member/import', data: { rows } })
export const getClassInviteList = (classId?: number) =>
  educationRequest.get({ url: '/ai-education/member/invite/list', params: { classId } })
export const createClassInvite = (data: {
  schoolId: number
  classId: number
  teachingClassId?: number
  expireTime: string
  maxUses: number
}) => educationRequest.post({ url: '/ai-education/member/invite/create', data })
export const disableClassInvite = (id: number) =>
  educationRequest.put({ url: '/ai-education/member/invite/disable', params: { id } })

export const getDeviceList = (params?: { schoolId?: number; status?: number }) =>
  educationRequest.get({ url: '/ai-education/device/list', params })
export const registerDevice = (data: any) =>
  educationRequest.post({ url: '/ai-education/device/register', data })
export const bindDevice = (data: { deviceId: number; userId: number; userType: number }) =>
  educationRequest.post({ url: '/ai-education/device/bind', data })
export const sendDeviceCommand = (data: any) =>
  educationRequest.post({ url: '/ai-education/device/command', data })
export const getDeviceCommandList = (deviceId: number) =>
  educationRequest.get({ url: '/ai-education/device/command/list', params: { deviceId } })
export const getDeviceSyncLogList = (deviceId: number) =>
  educationRequest.get({ url: '/ai-education/device/sync/list', params: { deviceId } })

export const getPilotProjectList = (schoolId?: number) =>
  educationRequest.get({
    url: '/ai-education/pilot/project/list',
    params: { schoolId },
    headers: { ignoreError: true }
  })
export const savePilotProject = (data: any) =>
  educationRequest.post({ url: '/ai-education/pilot/project/save', data })
export const getPilotIssueList = (params?: { pilotId?: number; status?: number }) =>
  educationRequest.get({ url: '/ai-education/pilot/issue/list', params })
export const getPilotMetricList = (pilotId: number) =>
  educationRequest.get({ url: '/ai-education/pilot/metric/list', params: { pilotId } })
export const recordPilotMetric = (data: any) =>
  educationRequest.post({ url: '/ai-education/pilot/metric', data })
export const savePilotIssue = (data: any) =>
  educationRequest.post({ url: '/ai-education/pilot/issue/save', data })
export const resolvePilotIssue = (data: { issueId: number; resolution: string }) =>
  educationRequest.put({ url: '/ai-education/pilot/issue/resolve', data })
export const exportAnalyticsCsv = (params?: Record<string, any>) =>
  educationRequest.download({ url: '/ai-education/analytics/export-csv', params })
export const getResourceTaskList = (trainCourseId?: number) =>
  educationRequest.get({ url: '/ai-education/resource/list', params: { trainCourseId } })
export const createResourceTask = (data: {
  schoolId: number
  trainCourseId: number
  chapterId?: number
  knowledgeId: number
  fileName: string
  fileUrl: string
  fileType: string
  fileSize?: number
  segmentMaxTokens?: number
}) => educationRequest.post({ url: '/ai-education/resource/create', data })
export const retryResourceTask = (id: number) =>
  educationRequest.put({ url: '/ai-education/resource/retry', params: { id } })
export const generateLessonPrep = (data: {
  schoolId: number
  trainCourseId: number
  chapterId?: number
  contentType: number
  topic: string
  language?: string
}) => educationRequest.post({ url: '/ai-education/lesson-prep/generate', data })
